
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import basicDemo from './demo/basic.twig';
// The '!!raw-loader!' syntax is a non-standard, Webpack-specific, syntax.
// See: https://github.com/webpack-contrib/raw-loader#examples
// For now, it seems likely Storybook is pretty tied to Webpack, therefore, we
// are okay with the following Webpack-specific raw loader syntax. It's better
// to leave the ESLint rule enabled globally, and only thoughtfully disable as
// needed (e.g. within a Storybook docs page and not within an actual
// component). This can be revisited in the future if Storybook no longer relies
// on Webpack.
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import fillDemoSource from '!!raw-loader!./demo/fill.twig';
import fillDemo from './demo/fill.twig';
const basicDemoStory = (args) => {
  const modifiers = [];
  if (args.prose) {
    modifiers.push('prose');
  }
  if (args.pad && args.pad.length > 0) {
    if (args.pad.length === 2) {
      modifiers.push('pad');
    } else {
      modifiers.push(`pad-${args.pad[0]}`);
    }
  }
  const classNames = modifiers.map((modifier) => `o-container--${modifier}`);
  return basicDemo({ ...args, class: classNames.join(' ') });
};
const padOptions = ['block', 'inline'];


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    {
      /*
      Inline stories disabled so media queries will behave as expected within
      embedded examples.
      */
    }
    <Meta title="Objects/Container" parameters={{
      layout: 'fullscreen',
      docs: {
        inlineStories: false
      }
    }} argTypes={{
      prose: {
        control: {
          type: 'boolean'
        }
      },
      pad: {
        options: padOptions,
        control: {
          type: 'inline-check'
        }
      }
    }} mdxType="Meta" />
    <h1>{`Containers`}</h1>
    <p>{`Containers help maintain consistent reading line lengths and prevent fixed-width imagery from expanding to unwieldy sizes by establishing a maximum width. When the viewport exceeds that maximum width, the content is centered.`}</p>
    <p>{`The container's default width is generous, a good fit for content that can expand to fill multiple columns. For example, a grid of article summaries.`}</p>
    {
      /*
      The object demo is not particularly helpful if its source is imported directly,
      so we've provided it manually to the docs add-on via parameters.
      */
    }
    <Canvas mdxType="Canvas">
  <Story name="Basic" height="300px" args={{
        prose: false,
        pad: padOptions
      }} parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/objects/container/container.twig' with {
  class: 'o-container--pad'
} only %}
  {% block content %}
    {# content #}
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {basicDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Prose`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`--prose`}</inlineCode>{` modifier is intended to be used with content that mostly consists of text, like an article. It helps improve readability by limiting line length.`}</p>
    {
      /*
      The object demo is not particularly helpful if its source is imported directly,
      so we've provided it manually to the docs add-on via parameters.
      */
    }
    <Canvas mdxType="Canvas">
  <Story name="Prose" height="300px" args={{
        prose: true,
        pad: padOptions
      }} parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/objects/container/container.twig' with {
  class: 'o-container--prose o-container--pad'
} only %}
  {% block content %}
    {# content #}
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {basicDemoStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Padding`}</h2>
    <p>{`By default, container does not apply any padding. This means the content will meet the edges of the viewport below the maximum size.`}</p>
    <p>{`Modifiers may be added to apply a consistent amount of responsive padding to containers:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`o-container--pad`}</inlineCode>{`: Adds padding on all sides.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`o-container--pad-block`}</inlineCode>{`: Adds vertical padding.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`o-container--pad-inline`}</inlineCode>{`: Adds horizontal padding.`}</li>
    </ul>
    <p>{`Custom padding amounts may be applied through the use of `}<a parentName="p" {...{
        "href": "/docs/utilities-spacing--padding"
      }}>{`padding utilities`}</a>{`.`}</p>
    <h2>{`Fill`}</h2>
    <p>{`There are times when a child element should attempt to fill the container content width, including padding. This can highlight visual elements (images and embeds) and align content within containers.`}</p>
    <p>{`Two child element classes exist for this purpose:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`o-container__pad`}</inlineCode>{`: Adds the standard container padding to a child element.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`o-container__fill`}</inlineCode>{`: Negates any inline padding so the element will reach the edges of the content container's padding.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`o-container__fill-pad`}</inlineCode>{`: Also applies padding to the element so its content will align with that of the container.`}</li>
    </ul>
    <Canvas mdxType="Canvas">
  <Story name="Fill" height="500px" argTypes={{}} parameters={{
        docs: {
          source: {
            code: fillDemoSource
          }
        }
      }} mdxType="Story">
    {fillDemo.bind({})}
  </Story>
    </Canvas>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const basic = basicDemoStory.bind({});
basic.storyName = 'Basic';
basic.args = {
  prose: false,
  pad: padOptions
};
basic.parameters = { storySource: { source: 'basicDemoStory.bind({})' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/objects/container/container.twig' with {
  class: 'o-container--pad'
} only %}
  {% block content %}
    {# content #}
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const prose = basicDemoStory.bind({});
prose.storyName = 'Prose';
prose.args = {
  prose: true,
  pad: padOptions
};
prose.parameters = { storySource: { source: 'basicDemoStory.bind({})' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/objects/container/container.twig' with {
  class: 'o-container--prose o-container--pad'
} only %}
  {% block content %}
    {# content #}
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const fill = fillDemo.bind({});
fill.storyName = 'Fill';
fill.argTypes = {};
fill.parameters = { storySource: { source: 'fillDemo.bind({})' }, ...{
  docs: {
    source: {
      code: fillDemoSource
    }
  }
} };

const componentMeta = { title: 'Objects/Container', parameters: {
  layout: 'fullscreen',
  docs: {
    inlineStories: false
  }
}, argTypes: {
  prose: {
    control: {
      type: 'boolean'
    }
  },
  pad: {
    options: padOptions,
    control: {
      type: 'inline-check'
    }
  }
}, includeStories: ["basic","prose","fill"],  };

const mdxStoryNameToKey = {"Basic":"basic","Prose":"prose","Fill":"fill"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;